import { useEffect, useState } from "react";
import {
  useTask,
  useUpdateTask,
  useCompleteTask,
  useDeleteTask,
  useLists,
  useSubtasks,
  useCreateTask,
} from "../../hooks/useTasks";
import { useUsers } from "../../hooks/useDirectory";
import { useUiStore } from "../../state/uiStore";
import { PRIO, STATUS, type Priority, type Status } from "../../lib/design";
import { dueDateToIso, isoToDayKey, isOverdue } from "../../lib/date";
import classNames from "classnames";
import { userLabel } from "../../hooks/useDirectory";
import { StatusChip } from "../chips";
import { AssignmentsSection } from "./AssignmentsSection";
import { AclSection } from "./AclSection";
import { BlockersSection } from "./BlockersSection";
import { CommentsSection } from "./CommentsSection";
import { AttachmentsSection } from "./AttachmentsSection";
import { ProvenanceSection } from "./ProvenanceSection";
import { recurrenceLabel } from "../../lib/recurrence";
import type { TaskVisibility } from "../../api/tm";

const selectStyle = {
  font: "inherit",
  fontSize: 12.5,
  border: "1px solid var(--border-3)",
  borderRadius: 6,
  padding: "2px 6px",
  background: "var(--bg)",
} as const;

export function TaskDetailPanel({ taskId }: { taskId: string }) {
  const selectTask = useUiStore((s) => s.selectTask);
  const task = useTask(taskId);
  const lists = useLists();
  const users = useUsers();
  const subtasks = useSubtasks(taskId);
  const updateTask = useUpdateTask();
  const completeTask = useCompleteTask();
  const deleteTask = useDeleteTask();
  const createTask = useCreateTask();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [tagDraft, setTagDraft] = useState("");
  const [subDraft, setSubDraft] = useState("");

  useEffect(() => {
    if (task.data) {
      setTitle(task.data.title);
      setDesc(task.data.description ?? "");
    }
  }, [task.data]);

  if (task.isLoading) {
    return (
      <aside className="detail-panel">
        <div className="detail-body">
          <div className="empty-state">Chargement…</div>
        </div>
      </aside>
    );
  }

  const t = task.data;
  if (!t) {
    return (
      <aside className="detail-panel">
        <div className="detail-body">
          <div className="empty-state">
            Tâche introuvable ou lecture seule (work item PM répliqué —
            ouvrez-le dans Project Management).
          </div>
          <button className="btn" onClick={() => selectTask(null)}>
            Fermer
          </button>
        </div>
      </aside>
    );
  }

  const patch = (body: Parameters<typeof updateTask.mutate>[0]["body"]) =>
    updateTask.mutate({ id: t.id, body });

  const saveTitle = () => {
    const v = title.trim();
    if (v && v !== t.title) patch({ title: v });
  };

  const addTag = () => {
    const v = tagDraft.trim();
    if (!v) return;
    patch({ tags: [...(t.tags ?? []), v] });
    setTagDraft("");
  };

  const addSubtask = () => {
    const v = subDraft.trim();
    if (!v) return;
    createTask.mutate({
      title: v,
      parent_task_id: t.id,
      visibility: t.visibility,
    });
    setSubDraft("");
  };

  const done = t.status === "done";

  return (
    <aside className="detail-panel">
      <div className="detail-body">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <StatusChip
            status={(t.status in STATUS ? t.status : "todo") as Status}
          />
          <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button
              className="btn"
              onClick={() =>
                done ? patch({ status: "todo" }) : completeTask.mutate(t.id)
              }
            >
              {done ? "Rouvrir" : "✓ Terminer"}
            </button>
            <button
              className="btn danger"
              onClick={() => {
                deleteTask.mutate(t.id);
                selectTask(null);
              }}
              title="Supprimer la tâche"
            >
              🗑
            </button>
            <button
              className="btn"
              onClick={() => selectTask(null)}
              aria-label="Fermer"
            >
              ✕
            </button>
          </span>
        </div>

        <textarea
          className="detail-title"
          rows={2}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) =>
            e.key === "Enter" && (e.preventDefault(), saveTitle())
          }
          aria-label="Titre de la tâche"
        />

        <div style={{ marginTop: 8 }}>
          <div className="prop-row">
            <span className="prop-label">Statut</span>
            <span className="prop-value chip-picker" role="radiogroup">
              {Object.entries(STATUS)
                .filter(([k]) => k !== "archived")
                .map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={t.status === k}
                    className={classNames("chip", "chip-choice", {
                      active: t.status === k,
                    })}
                    style={
                      t.status === k
                        ? { color: "#fff", background: v.dot }
                        : undefined
                    }
                    onClick={() => patch({ status: k as Status })}
                  >
                    {v.label}
                  </button>
                ))}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Priorité</span>
            <span className="prop-value chip-picker" role="radiogroup">
              {Object.entries(PRIO).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={t.priority === k}
                  className={classNames("chip", "chip-choice", {
                    active: t.priority === k,
                  })}
                  style={
                    t.priority === k
                      ? { color: v.color, background: v.bg }
                      : undefined
                  }
                  onClick={() => patch({ priority: k as Priority })}
                >
                  {v.label}
                </button>
              ))}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Échéance</span>
            <span
              className="prop-value"
              style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
            >
              <input
                type="date"
                style={selectStyle}
                value={t.due_date ? isoToDayKey(t.due_date) : ""}
                onChange={(e) =>
                  patch({
                    due_date: e.target.value
                      ? dueDateToIso(e.target.value)
                      : null,
                  })
                }
                aria-label="Échéance"
              />
              {t.due_date && !done && isOverdue(isoToDayKey(t.due_date)) && (
                <span
                  className="chip"
                  style={{
                    color: "var(--danger)",
                    background: "var(--danger-soft)",
                  }}
                >
                  en retard
                </span>
              )}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Rappel</span>
            <span className="prop-value">
              {t.due_date ? (
                <ReminderEditor
                  dueDate={t.due_date}
                  reminderAt={t.reminder_at}
                  onChange={(iso) => patch({ reminder_at: iso })}
                />
              ) : (
                <span style={{ color: "var(--ink-ghost)", fontSize: 11.5 }}>
                  Définissez une échéance d'abord
                </span>
              )}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Liste</span>
            <span className="prop-value">
              {lists.data?.find((l) => l.id === t.list_id)?.name ?? (
                <span style={{ color: "var(--ink-ghost)" }}>Aucune</span>
              )}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Visibilité</span>
            <span className="prop-value">
              <select
                style={selectStyle}
                value={t.visibility}
                onChange={(e) =>
                  patch({ visibility: e.target.value as TaskVisibility })
                }
                aria-label="Visibilité"
              >
                <option value="private">Privée</option>
                <option value="team">Équipe</option>
                <option value="tenant">Tenant</option>
              </select>
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Récurrence</span>
            <span className="prop-value">
              {/* Fixée à la création — le PATCH TM n'expose pas recurrence_rule. */}
              {t.recurrence_rule ? (
                <>
                  {recurrenceLabel(t.recurrence_rule)}
                  <span
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: "var(--ink-ghost)",
                      marginLeft: 6,
                    }}
                  >
                    {t.recurrence_rule}
                  </span>
                </>
              ) : (
                <span style={{ color: "var(--ink-ghost)" }}>Aucune</span>
              )}
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Demandeur</span>
            <span className="prop-value">
              {t.reporter_actor_ref
                ? userLabel(users.data, t.reporter_actor_ref)
                : userLabel(users.data, t.created_by_user_ref)}
              <span
                style={{
                  fontSize: 10.5,
                  color: "var(--ink-ghost)",
                  marginLeft: 6,
                }}
              >
                reporter
              </span>
            </span>
          </div>
          <div className="prop-row">
            <span className="prop-label">Tags</span>
            <span
              className="prop-value"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "center",
              }}
            >
              {(t.tags ?? []).map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                  <button
                    style={{
                      marginLeft: 4,
                      color: "var(--ink-ghost)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      patch({ tags: t.tags.filter((x) => x !== tag) })
                    }
                    aria-label={`Retirer ${tag}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                value={tagDraft}
                placeholder="+ tag"
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()}
                style={{ ...selectStyle, width: 70 }}
                aria-label="Ajouter un tag"
              />
            </span>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-title">Description</div>
          <textarea
            className="comment-input"
            rows={3}
            value={desc}
            placeholder="Ajouter une description…"
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => {
              const v = desc.trim();
              if (v !== (t.description ?? "")) patch({ description: v });
            }}
            aria-label="Description de la tâche"
          />
        </div>

        <ProvenanceSection task={t} />
        <AssignmentsSection taskId={t.id} />

        <div className="detail-section">
          {t.parent_task_id && (
            <button
              className="btn"
              style={{ marginBottom: 8 }}
              onClick={() => selectTask(t.parent_task_id!)}
            >
              ‹ Voir la tâche parente
            </button>
          )}
          <div
            className="detail-section-title"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            Sous-tâches
            {(subtasks.data ?? []).length > 0 && (
              <>
                <span style={{ fontWeight: 500 }}>
                  {
                    (subtasks.data ?? []).filter((s) => s.status === "done")
                      .length
                  }
                  /{(subtasks.data ?? []).length}
                </span>
                <span className="progress-bar" style={{ width: 70 }}>
                  <span
                    className="progress-fill"
                    style={{
                      width: `${Math.round(
                        ((subtasks.data ?? []).filter(
                          (s) => s.status === "done",
                        ).length /
                          (subtasks.data ?? []).length) *
                          100,
                      )}%`,
                    }}
                  />
                </span>
              </>
            )}
          </div>
          {(subtasks.data ?? []).map((s) => (
            <div
              key={s.id}
              className="prop-row"
              style={{ alignItems: "center" }}
            >
              <button
                className={`task-check${s.status === "done" ? " done" : ""}`}
                onClick={() =>
                  s.status === "done"
                    ? updateTask.mutate({ id: s.id, body: { status: "todo" } })
                    : completeTask.mutate(s.id)
                }
                aria-label={s.status === "done" ? "Rouvrir" : "Terminer"}
              >
                {s.status === "done" ? "✓" : ""}
              </button>
              <span
                className="prop-value"
                style={
                  s.status === "done"
                    ? {
                        textDecoration: "line-through",
                        color: "var(--ink-faint)",
                      }
                    : undefined
                }
              >
                {s.title}
              </span>
            </div>
          ))}
          {!t.parent_task_id && (
            <div className="quick-add" style={{ marginTop: 6 }}>
              <input
                value={subDraft}
                placeholder="Ajouter une sous-tâche…"
                onChange={(e) => setSubDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSubtask()}
                aria-label="Nouvelle sous-tâche"
              />
            </div>
          )}
        </div>

        <BlockersSection taskId={t.id} />
        <AclSection taskId={t.id} />
        <AttachmentsSection taskId={t.id} />
        <CommentsSection taskId={t.id} />
      </div>
    </aside>
  );
}

const REMINDER_OFFSETS: { value: string; label: string }[] = [
  { value: "", label: "Aucun" },
  { value: "0", label: "Le jour même" },
  { value: "1", label: "1 jour avant" },
  { value: "2", label: "2 jours avant" },
  { value: "3", label: "3 jours avant" },
  { value: "7", label: "1 semaine avant" },
  { value: "custom", label: "Date précise…" },
];

function shiftDayKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

/** Rappel par offset avant l'échéance (comme le prototype) → reminder_at. */
function ReminderEditor({
  dueDate,
  reminderAt,
  onChange,
}: {
  dueDate: string;
  reminderAt?: string | null;
  onChange: (iso: string | null) => void;
}) {
  const dueKey = isoToDayKey(dueDate);
  const remKey = reminderAt ? isoToDayKey(reminderAt) : null;
  const preset = remKey
    ? (REMINDER_OFFSETS.find(
        (o) =>
          o.value !== "" &&
          o.value !== "custom" &&
          shiftDayKey(dueKey, Number(o.value)) === remKey,
      )?.value ?? "custom")
    : "";
  const [custom, setCustom] = useState(preset === "custom");
  // Le panneau n'est pas remonté au changement de tâche — resync l'état local.
  useEffect(() => setCustom(preset === "custom"), [dueKey, remKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <select
        style={selectStyle}
        value={custom ? "custom" : preset}
        aria-label="Rappel"
        onChange={(e) => {
          const v = e.target.value;
          if (v === "custom") {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(
            v === "" ? null : dueDateToIso(shiftDayKey(dueKey, Number(v))),
          );
        }}
      >
        {REMINDER_OFFSETS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {(custom || preset === "custom") && (
        <input
          type="date"
          style={selectStyle}
          value={remKey ?? ""}
          max={dueKey}
          onChange={(e) =>
            onChange(e.target.value ? dueDateToIso(e.target.value) : null)
          }
          aria-label="Date de rappel"
        />
      )}
    </span>
  );
}
